import {
  FiPackage,
  FiRefreshCw,
  FiSearch,
  FiHelpCircle,
  FiWifiOff,
} from "react-icons/fi";
import { MdError, MdSignalWifiConnectedNoInternet4 } from "react-icons/md";
import PropTypes from "prop-types";
import CustomButton from 'components/ui/button/CustomButton';
import { renderIcon } from 'components/ui/button/CustomButton';

const StatusState = ({
  // Content props
  type = "empty", // 'empty', 'error', 'offline', 'custom'
  title = null,
  message = null,
  customIcon: Icon = null,
  iconColor = "inherit",

  // Action props
  primaryAction = null,
  secondaryAction = null,
  refreshAction = false,

  // Style props
  className = "",
  iconSize = "xl", // sm, md, lg, xl
  compact = false,
  fullScreen = true,

  // Footer props
  showSupportLink = false,
  supportLinkText = "Contact support",
  supportHref = "mailto:support@starrik.com",
}) => {
  // Default configurations for each type
  const typeConfigs = {
    empty: {
      icon: <FiPackage />,
      defaultTitle: "No content available",
      defaultMessage: "When content becomes available, it will appear here",
    },
    error: {
      icon: <MdError />,
      defaultTitle: "Something went wrong",
      defaultMessage: "We encountered an error while loading content",
    },
    offline: {
      icon: <MdSignalWifiConnectedNoInternet4 />,
      defaultTitle: "Connection lost",
      defaultMessage: "Please check your internet connection and try again",
    },
    custom: {
      icon: null,
      defaultTitle: "",
      defaultMessage: "",
    },
  };

  // Current type configuration
  const config = typeConfigs[type] || typeConfigs.empty;

  // Icon sizing
  const iconSizeMap = {
    sm: "text-3xl",
    md: "text-4xl",
    lg: "text-5xl",
    xl: "text-6xl",
  };

  // Container classes
  const containerClasses = `flex flex-col bg-white rounded-3xl shadow-sm items-center justify-center text-center ${
    fullScreen ? "h-screen" : "py-12"
  } px-4 ${className}`;

  return (
    <div className={containerClasses}>
      {/* Visual Element */}
      <div
        className={`mb-4 p-3 ${
          type === "error"
            ? "bg-red-50"
            : type === "offline"
            ? "bg-yellow-50"
            : "bg-gray-50"
        } rounded-full ${compact ? "scale-90" : ""}`}
      >
        {(Icon && renderIcon(Icon, "left", 34, iconColor)) || (
          <div
            className={`${
              type === "error"
                ? "text-red-400"
                : type === "offline"
                ? "text-yellow-400"
                : "text-gray_dark"
            } ${iconSizeMap[iconSize] || iconSizeMap.xl}`}
          >
            {config.icon}
          </div>
        )}
      </div>

      {/* Title & Message */}
      <div className={`mb-6 ${compact ? "max-w-sm" : "max-w-md"} mx-auto`}>
        <h1
          className={`${
            compact ? "text-xl" : "text-2xl"
          } font-semibold text-gray_dark mb-2 capitalize`}
        >
          {title || config.defaultTitle}
        </h1>
        <p className="text-gray-500 text-center">
          {message || config.defaultMessage}
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap justify-center gap-3 w-full">
        {refreshAction && (
          <CustomButton
            variant={type === "error" ? "danger" : "primary"}
            onClick={(() => window.location.reload())}
            icon={FiRefreshCw}
            size={compact ? "sm" : "md"}
            className=""
          >
            Refresh
          </CustomButton>
        )}

        {primaryAction && (
          <CustomButton
            variant="primary"
            onClick={primaryAction.action}
            icon={primaryAction.icon}
            size={compact ? "sm" : "md"}
            className=""
          >
            {primaryAction.text}
          </CustomButton>
        )}

        {secondaryAction && (
          <CustomButton
            variant="secondary"
            onClick={secondaryAction.action}
            icon={secondaryAction.icon}
            size={compact ? "sm" : "md"}
            className=""
          >
            {secondaryAction.text}
          </CustomButton>
        )}
      </div>

      {/* Support Link */}
      {showSupportLink && (
        <div
          className={`mt-6 text-sm text-gray-400 ${compact ? "text-xs" : ""}`}
        >
          <a
            href={supportHref}
            className="inline-flex items-center text-primary hover:underline"
          >
            <FiHelpCircle className="mr-1" />
            {supportLinkText}
          </a>
        </div>
      )}
    </div>
  );
};

StatusState.propTypes = {
  // Content
  type: PropTypes.oneOf(["empty", "error", "offline", "custom"]),
  title: PropTypes.string,
  message: PropTypes.string,
  customIcon: PropTypes.oneOfType([
    PropTypes.elementType,
    PropTypes.oneOf([null])
  ]),

  // Actions
  primaryAction: PropTypes.shape({
    text: PropTypes.string.isRequired,
    action: PropTypes.func.isRequired,
    icon: PropTypes.oneOfType([
    PropTypes.elementType,
    PropTypes.oneOf([null])
  ]),
  }),
  secondaryAction: PropTypes.shape({
    text: PropTypes.string.isRequired,
    action: PropTypes.func.isRequired,
    icon: PropTypes.oneOfType([
    PropTypes.elementType,
    PropTypes.oneOf([null])
  ]),
  }),
  refreshAction: PropTypes.bool,

  // Styles
  className: PropTypes.string,
  iconSize: PropTypes.oneOf(["sm", "md", "lg", "xl"]),
  compact: PropTypes.bool,
  fullScreen: PropTypes.bool,

  // Footer
  showSupportLink: PropTypes.bool,
  supportLinkText: PropTypes.string,
  supportHref: PropTypes.string,
};

// Convenience components
export const EmptyState = (props) => <StatusState type="empty" {...props} />;
export const ErrorState = (props) => <StatusState type="error" {...props} />;
export const OfflineState = (props) => (
  <StatusState type="offline" {...props} />
);

export default StatusState;
